import React, { useState, useRef, useEffect } from 'react';
import { Upload, Mic, Activity, Play, Pause, Trash2, X,User, Menu, CheckCircle, TrendingUp, Shield, Zap, Sparkles } from 'lucide-react';
import { api } from '../services/api';

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div style={{...styles.toast, ...(type === 'success' ? styles.toastSuccess : styles.toastError)}}>
      <div style={styles.toastIcon}>
        <CheckCircle size={18} />
      </div>
      <span>{message}</span>
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div style={styles.skeletonContainer}>
      <div style={styles.skeletonCircle} />
      <div style={styles.skeletonLine} />
      <div style={{...styles.skeletonLine, width: '60%'}} />
      <div style={styles.skeletonButton} />
    </div>
  );
}

function FreeTrialBanner() {
  return (
    <div style={styles.trialBanner}>
      <div style={styles.trialBannerContent}>
        <Sparkles size={20} color="#8B5CF6" />
        <span style={styles.trialBannerText}>
          Enjoy free unlimited access until <strong>March 2026</strong>
        </span>
      </div>
    </div>
  );
}

function PricingPlans() {
  const plans = [
    {
      name: 'Free',
      price: '₹0',
      period: '/forever',
      features: ['3 detections per day', 'All detection methods', 'Basic support', 'Community access'],
      highlighted: false,
      badge: 'Always Free',
    },
    {
      name: 'Monthly',
      price: '₹49',
      period: '/month',
      features: ['Unlimited analyses', 'All detection methods', 'History tracking', 'Email support'],
      highlighted: false,
    },
    {
      name: '6 Months',
      price: '₹299',
      period: '/6 months',
      features: ['Everything in Monthly', 'Priority support', 'Advanced analytics', 'Export data'],
      highlighted: true,
      badge: 'Best Value',
      savings: 'Save ₹95 (24%)',
    },
  ];

  return (
    <div style={styles.pricingSection}>
      <div style={styles.pricingHeader}>
        <h2 style={styles.pricingTitle}>Simple, transparent pricing</h2>
        <p style={styles.pricingSubtitle}>Choose the plan that works for your family</p>
        <div style={styles.availabilityBadge}>Available March 2026</div>
      </div>
      
      <div style={styles.pricingGrid}>
        {plans.map((plan, index) => (
          <div 
            key={index} 
            style={{
              ...styles.pricingCard,
              ...(plan.highlighted && styles.pricingCardHighlighted)
            }}
            className="pricingCard"
          >
            {plan.badge && (
              <div style={styles.pricingBadge}>{plan.badge}</div>
            )}
            <h3 style={styles.planName}>{plan.name}</h3>
            <div style={styles.planPrice}>
              <span style={styles.priceAmount}>{plan.price}</span>
              <span style={styles.pricePeriod}>{plan.period}</span>
            </div>
            {plan.savings && (
              <div style={styles.savingsBadge}>{plan.savings}</div>
            )}
            <ul style={styles.featureList}>
              {plan.features.map((feature, idx) => (
                <li key={idx} style={styles.featureItem}>
                  <CheckCircle size={18} color={plan.highlighted ? '#8B5CF6' : '#10B981'} />
                  <span style={styles.featureText}>{feature}</span>
                </li>
              ))}
            </ul>
            <button 
              style={{
                ...styles.planButton,
                ...(plan.highlighted && styles.planButtonHighlighted)
              }}
            >
              Coming Soon
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultDisplay({ result }) {
  const [isVisible, setIsVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
    setTimeout(() => setShowSuccess(true), 300);
  }, []);

  const categoryInfo = {
    hungry: { 
      icon: '🍼', 
      color: '#8B5CF6', 
      bg: '#F3E8FF', 
      label: 'Hungry', 
      advice: 'Your baby might need feeding',
      instructions: [
        'Check when your baby last fed - newborns need feeding every 2-3 hours',
        'Prepare a bottle or get comfortable for breastfeeding in a quiet space',
        'Ensure proper latching and watch for swallowing cues',
        'Burp your baby halfway through and after feeding to prevent discomfort',
        'Keep track of feeding times to establish a routine'
      ]
    },
    tired: { 
      icon: '😴', 
      color: '#6366F1', 
      bg: '#E0E7FF', 
      label: 'Tired', 
      advice: 'Time for a nap or bedtime routine',
      instructions: [
        'Create a calm, dimly lit environment to signal sleep time',
        'Swaddle your baby snugly to provide comfort and security',
        'Use gentle rocking or rhythmic patting to soothe them',
        'Play soft white noise or lullabies to mask household sounds',
        'Watch for sleep cues like eye rubbing or yawning and act promptly'
      ]
    },
    belly_pain: { 
      icon: '😣', 
      color: '#EF4444', 
      bg: '#FEE2E2', 
      label: 'Belly Pain', 
      advice: 'Gentle tummy massage may help',
      instructions: [
        'Lay baby on their back and gently massage tummy in clockwise circles',
        'Try "bicycle legs" - gently move their legs in a cycling motion',
        'Hold baby against your chest with gentle pressure on their tummy',
        'A warm (not hot) compress on the belly can provide relief',
        'If pain persists beyond 2 hours or baby has fever, contact your pediatrician'
      ]
    },
    burping: { 
      icon: '💨', 
      color: '#F59E0B', 
      bg: '#FEF3C7', 
      label: 'Needs Burping', 
      advice: 'Try gentle burping positions',
      instructions: [
        'Hold baby upright against your chest and gently pat or rub their back',
        'Try sitting baby on your lap, supporting their chest and chin, then pat back',
        'Lay baby face-down across your lap and gently rub their back',
        'Burp after every 2-3 ounces during bottle feeding',
        'If no burp after 5 minutes, resume feeding and try again later'
      ]
    },
    discomfort: { 
      icon: '😢', 
      color: '#EC4899', 
      bg: '#FCE7F3', 
      label: 'Discomfort', 
      advice: 'Check diaper or adjust clothing',
      instructions: [
        'Check if diaper is wet or soiled and change immediately if needed',
        'Ensure clothing isn\'t too tight, scratchy, or causing overheating',
        'Check for any hair wrapped around fingers or toes (hair tourniquet)',
        'Adjust room temperature - babies are comfortable at 68-72°F (20-22°C)',
        'Look for any signs of rash, irritation, or discomfort that need attention'
      ]
    }
  };

  const info = categoryInfo[result.predicted_class] || categoryInfo.discomfort;
  const probabilities = Object.entries(result.all_probabilities || {})
    .sort(([, a], [, b]) => b - a);

  return (
    <>
      <FreeTrialBanner />
      <div style={{
        ...styles.resultContainer,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
      {showSuccess && (
        <div style={styles.successBadge}>
          Analysis Complete ✓
        </div>
      )}
        
        <div style={styles.resultCard}>
          <div style={{...styles.resultIcon, backgroundColor: info.bg}}>
            <span style={styles.resultEmoji}>{info.icon}</span>
          </div>
          
          <h3 style={styles.resultTitle}>{info.label}</h3>
          <p style={styles.resultAdvice}>{info.advice}</p>
          
          <div style={styles.confidenceBadge}>
            <span style={styles.confidenceLabel}>Confidence</span>
            <div style={styles.confidenceValueWrapper}>
              <span style={styles.confidenceValue}>{(result.confidence * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div style={styles.probabilitiesCard}>
          <h4 style={styles.probabilitiesTitle}>Detailed Analysis</h4>
          <div style={styles.probabilitiesList}>
            {probabilities.map(([category, prob], index) => {
              const catInfo = categoryInfo[category];
              return (
                <div key={category} style={{
                  ...styles.probabilityItem,
                  animation: `slideIn 0.4s ease-out ${index * 0.1}s backwards`
                }}>
                  <div style={styles.probabilityLabel}>
                    <span style={styles.probabilityEmoji}>{catInfo?.icon}</span>
                    <span style={styles.probabilityName}>{catInfo?.label}</span>
                  </div>
                  <div style={styles.probabilityBar}>
                    <div 
                      style={{
                        ...styles.probabilityFill,
                        width: `${prob * 100}%`,
                        backgroundColor: catInfo?.color
                      }}
                    />
                  </div>
                  <span style={styles.probabilityPercent}>{(prob * 100).toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={styles.trustIndicators}>
          <div style={styles.trustItem}>
            <Shield size={18} color="#10B981" />
            <span>Secure Analysis</span>
          </div>
          <div style={styles.trustItem}>
            <Zap size={18} color="#F59E0B" />
            <span>Instant Results</span>
          </div>
          <div style={styles.trustItem}>
            <TrendingUp size={18} color="#6366F1" />
            <span>AI-Powered</span>
          </div>
        </div>

        {/* Care Instructions Section */}
        <div style={styles.careInstructionsCard}>
          <div style={styles.careInstructionsHeader}>
            <Sparkles size={20} color="#8B5CF6" />
            <h4 style={styles.careInstructionsTitle}>What To Do Next</h4>
          </div>
          
          <div style={styles.careInstructionsContent}>
            <ol style={styles.instructionsList}>
              {info.instructions.map((instruction, index) => (
                <li key={index} style={styles.instructionItem}>
                  <span style={styles.instructionNumber}>{index + 1}</span>
                  <span style={styles.instructionText}>{instruction}</span>
                </li>
              ))}
            </ol>
          </div>

          <div style={styles.emergencyNote}>
            <div style={styles.emergencyContent}>
              <h5 style={styles.emergencyTitle}>When to Seek Immediate Medical Help</h5>
              <p style={styles.emergencyText}>
                    If your baby cries for over 2–3 hours, has a fever, trouble breathing, is very sleepy, won’t feed,
                    or shows unusual symptoms, contact your pediatrician right away.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
function UploadTab() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (selectedFile) => {
    const validTypes = ['audio/wav', 'audio/mpeg', 'audio/mp4', 'audio/flac', 'audio/ogg', 'audio/x-m4a'];
    
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(wav|mp3|m4a|flac|ogg)$/i)) {
      setError('Invalid file type. Please upload WAV, MP3, M4A, FLAC, or OGG');
      setToast({ message: 'Invalid file type', type: 'error' });
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB');
      setToast({ message: 'File too large', type: 'error' });
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResult(null);
    setToast({ message: 'File uploaded successfully', type: 'success' });
  };

  const handlePredict = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.predictUpload(file, 0.6);
      
      if (response.success) {
        setResult(response.data.prediction);
        setToast({ message: 'Analysis completed', type: 'success' });
      } else {
        throw new Error(response.error || 'Prediction failed');
      }
    } catch (err) {
      console.error('Prediction error:', err);
      setError(err.message || 'Failed to analyze audio');
      setToast({ message: 'Analysis failed', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.tabContent}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div
        style={{...styles.uploadArea, ...(dragActive && styles.uploadAreaActive)}}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div style={styles.uploadIcon}>
          <Upload size={48} color="#8B5CF6" />
        </div>
        
        <h3 style={styles.uploadTitle}>
          {file ? file.name : 'Drop your audio file here'}
        </h3>
        <p style={styles.uploadDescription}>
          or click to browse your files
        </p>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".wav,.mp3,.m4a,.flac,.ogg"
          onChange={(e) => e.target.files[0] && handleFileChange(e.target.files[0])}
          style={{ display: 'none' }}
        />
        
        <button
          onClick={() => fileInputRef.current?.click()}
          style={styles.uploadButton}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          Select file
        </button>
        
        <p style={styles.uploadNote}>
          Supported: WAV, MP3, M4A, FLAC, OGG · Max 10MB
        </p>
      </div>

      {file && (
        <div style={styles.filePreview}>
          <div style={styles.fileInfo}>
            <div style={styles.fileIconWrapper}>
              <Activity size={20} color="#8B5CF6" />
            </div>
            <div>
              <p style={styles.fileName}>{file.name}</p>
              <p style={styles.fileSize}>{(file.size / 1024).toFixed(2)} KB</p>
            </div>
          </div>
          <button
            onClick={() => {
              setFile(null);
              setResult(null);
            }}
            style={styles.fileRemove}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#FEE2E2'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {error && (
        <div style={styles.errorMessage}>
          <p>{error}</p>
        </div>
      )}

      {file && !result && (
        <button
          onClick={handlePredict}
          disabled={loading}
          style={{...styles.predictButton, ...(loading && styles.predictButtonDisabled)}}
        >
          {loading ? (
            <div style={styles.buttonContent}>
              <div style={styles.buttonSpinner} />
              <span>Analyzing audio...</span>
            </div>
          ) : 'Analyze audio'}
        </button>
      )}

      {!result && (
        <div style={styles.warningBanner}>
          <div style={styles.warningContent}>
            <span style={styles.warningTitle}>Important Note</span>
            <span style={styles.warningText}>
              Human voice or background noise may affect detection accuracy. For best results, ensure only baby crying sounds are present in the audio.
            </span>
          </div>
        </div>
      )}

      {loading && <SkeletonLoader />}
      {result && !loading && <ResultDisplay result={result} />}
      
      {result && (
        <div style={styles.warningBanner}>
          <div style={styles.warningContent}>
            <span style={styles.warningTitle}>Important Note</span>
            <span style={styles.warningText}>
              Human voice or background noise may affect detection accuracy. For best results, ensure only baby crying sounds are present in the audio.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordTab() {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [toast, setToast] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        } 
      });
      
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
        setToast({ message: 'Recording saved', type: 'success' });
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      setResult(null);
      setError(null);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Microphone error:', err);
      setError('Microphone access denied. Please allow microphone permissions.');
      setToast({ message: 'Microphone access denied', type: 'error' });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handlePredict = async () => {
    if (!audioBlob) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.predictRecord(audioBlob, 0.6);
      
      if (response.success) {
        setResult(response.data.prediction);
        setToast({ message: 'Analysis completed', type: 'success' });
      } else {
        throw new Error(response.error || 'Prediction failed');
      }
    } catch (err) {
      console.error('Prediction error:', err);
      setError(err.message || 'Failed to analyze recording');
      setToast({ message: 'Analysis failed', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clearRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setResult(null);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={styles.tabContent}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div style={styles.recordControl}>
        <div style={styles.recordIconWrapper}>
          <div style={{...styles.recordIcon, ...(isRecording && styles.recordIconActive)}}>
            <Mic size={40} color={isRecording ? '#EF4444' : '#8B5CF6'} />
          </div>
        </div>

        {isRecording && (
          <div style={styles.recordTime}>
            {formatTime(recordingTime)}
          </div>
        )}

        <div style={styles.recordControls}>
          {!isRecording && !audioBlob ? (
            <button
              onClick={startRecording}
              disabled={loading}
              style={styles.recordButton}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Start Recording
            </button>
          ) : isRecording ? (
            <button
              onClick={stopRecording}
              style={{...styles.recordButton, ...styles.recordButtonStop}}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Stop Recording
            </button>
          ) : null}
        </div>

        <p style={styles.recordNote}>
          {isRecording 
            ? 'Recording in progress...' 
            : 'Record at least 3 seconds of audio for best results'}
        </p>
      </div>

      {audioUrl && (
        <div style={styles.audioPreview}>
          <div style={styles.audioPreviewHeader}>
            <h4 style={styles.audioPreviewTitle}>Recorded Audio</h4>
            <button
              onClick={clearRecording}
              style={styles.audioDelete}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#FEE2E2'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <Trash2 size={18} />
            </button>
          </div>
          
          <div style={styles.audioPlayer}>
            <audio 
              ref={audioRef} 
              src={audioUrl} 
              onEnded={() => setIsPlaying(false)}
              style={{ display: 'none' }}
            />
            
            <button
              onClick={togglePlayback}
              style={styles.playButton}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
            
            <div style={styles.audioInfo}>
              <div style={styles.audioMeta}>
                <span>Duration: {formatTime(recordingTime)}</span>
                <span>{isPlaying ? 'Playing' : 'Paused'}</span>
              </div>
              <div style={styles.progressBar}>
                <div 
                  style={{
                    ...styles.progressFill,
                    width: isPlaying ? '100%' : '0%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={styles.errorMessage}>
          <p>{error}</p>
        </div>
      )}

      {audioBlob && !result && (
        <button
          onClick={handlePredict}
          disabled={loading}
          style={{...styles.predictButton, ...(loading && styles.predictButtonDisabled)}}
        >
          {loading ? (
            <div style={styles.buttonContent}>
              <div style={styles.buttonSpinner} />
              <span>Analyzing audio...</span>
            </div>
          ) : 'Analyze Recording'}
        </button>
      )}

      {!result && (
        <div style={styles.warningBanner}>
          <div style={styles.warningContent}>
            <span style={styles.warningTitle}>Important Note</span>
            <span style={styles.warningText}>
              Human voice or background noise may affect detection accuracy. For best results, ensure only baby crying sounds are present in the audio.
            </span>
          </div>
        </div>
      )}

      {loading && <SkeletonLoader />}
      {result && !loading && <ResultDisplay result={result} />}
      
      {result && (
        <div style={styles.warningBanner}>
          <div style={styles.warningContent}>
            <span style={styles.warningTitle}>Important Note</span>
            <span style={styles.warningText}>
              Human voice or background noise may affect detection accuracy. For best results, ensure only baby crying sounds are present in the audio.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PredictionPage() {
  const [activeTab, setActiveTab] = useState('record');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);


useEffect(() => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    setUser(JSON.parse(storedUser));
  }
}, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
    useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && ['upload', 'record'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleHomeClick = () => {
    window.location.href = '/';
  };

  const scrollToFooter = () => {
    const footer = document.getElementById('about');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const tabs = [
    { id: 'upload', label: 'Upload', desc: 'Upload audio files', icon: Upload },
    { id: 'record', label: 'Record', desc: 'Record in real-time', icon: Mic },
  ];

  return (
    <div style={styles.page}>
      <style>{keyframes}</style>
{/*navbar*/}
<nav style={{...styles.navbar, ...(scrolled && styles.navbarScrolled)}}>
  <div style={styles.navbarContainer}>
    <div style={styles.navbarContent}>
      <div style={styles.navbarLogo} onClick={handleHomeClick}>
        <img 
          src="logo.png" 
          alt="DontCry AI Logo" 
          style={styles.navbarLogoImage}
        />
        <span style={{
          fontSize: '18px',
          fontWeight: '600',
          fontFamily: 'Poppins, sans-serif',
          letterSpacing: '0px',
          color: '#black'
        }}>
          DontCry <span style={{fontWeight: '400'}}>ai</span>
        </span>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap" rel="stylesheet"></link>
      </div>
      
      <div style={styles.navbarMenu} className="navbarMenu">
        <button onClick={handleHomeClick} style={styles.navbarLink}>
          Home
        </button>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={styles.navbarLink}>
          Detect
        </button>
        <button onClick={scrollToFooter} style={styles.navbarLink}>
          About
        </button>

        {/* SIGN IN / PROFILE */}
        {!user ? (
          <button 
            onClick={() => window.location.href = '/signin'} 
            style={styles.signInButton}
          >
            Sign In
          </button>
        ) : (
          <div style={{ position: 'relative' }}>
            <div 
              style={styles.profileCircle}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
            </div>

            {/* DROPDOWN */}
            {dropdownOpen && (
              <div style={styles.dropdownMenu}>
                <div style={styles.dropdownHeader}>
                  <div style={styles.dropdownAvatar}>
                    {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div style={styles.dropdownUserInfo}>
                    <div style={styles.dropdownEmail}>{user.email}</div>
                  </div>
                </div>
                <div style={styles.dropdownDivider}></div>
                <button 
                  style={styles.dropdownItem}
                  onClick={() => {
                    localStorage.removeItem("user");
                    window.location.reload();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={styles.mobileHeaderActions}>
<div style={styles.mobileHeaderActions} className="mobileHeaderActions">
<div style={styles.mobileHeaderActions} className="mobileHeaderActions">
  {!user && (
    <button 
      onClick={() => window.location.href = '/signin'} 
      style={styles.mobileSignInButton}
      className="mobileSignInButton"
    >
      Sign In
    </button>
  )}
  <button 
    style={styles.mobileMenuButtonStyle}
    className="mobileMenuButton"
    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
  >
    {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
  </button>
</div>
</div>
</div>
    </div>
  </div>

  {mobileMenuOpen && (
  <div style={styles.mobileMenu} className="mobileMenu">
    <button onClick={() => { handleHomeClick(); setMobileMenuOpen(false); }} style={styles.mobileMenuLink}>
      Home
    </button>
    <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); }} style={styles.mobileMenuLink}>
      Detect
    </button>
    <button onClick={() => { scrollToFooter(); setMobileMenuOpen(false); }} style={styles.mobileMenuLink}>
      About
    </button>

    {/* MOBILE SIGN IN / PROFILE */}
    {!user ? (
      <>
        <div style={styles.mobileMenuDivider}></div>
        <button 
          onClick={() => window.location.href = '/signin'} 
          style={styles.mobileMenuSignInButton}
        >
          Sign In
        </button>
      </>
    ) : (
      <div style={styles.mobileUserSection}>
        <div style={styles.mobileUserInfo}>
          <div style={styles.mobileAvatar}>
            {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
          </div>
          <div style={styles.mobileEmailText}>{user.email}</div>
        </div>
        <button 
          onClick={() => {
            localStorage.removeItem("user");
            window.location.reload();
          }}
          style={styles.mobileLogoutButton}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          Logout
        </button>
      </div>
    )}
  </div>
)}
</nav>      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            Analyze your <span style={styles.titleHighlight}>baby's cry</span>
          </h1>
          <p style={styles.subtitle}>
            Choose your preferred detection method
          </p>
        </div>

        <div style={styles.card}>
          <div style={styles.tabs}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    ...styles.tabButton,
                    ...(activeTab === tab.id && styles.tabButtonActive)
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <Icon size={20} />
                  <div style={styles.tabText}>
                    <span style={styles.tabLabel}>{tab.label}</span>
                    <span style={styles.tabDesc} className="tabDesc">{tab.desc}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={styles.tabContentWrapper}>
            {activeTab === 'upload' && <UploadTab />}
            {activeTab === 'record' && <RecordTab />}
          </div>
        </div>

        <div style={styles.helpfulNote}>
          <span> </span>
        <span style={styles.noteText}>
         AI can make mistakes. Treat results as suggestions and consult your pediatrician for concerns.
          </span>
      </div>

        <PricingPlans />
      </div>

      <footer id="about" style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerBrand}>
            <h3 style={styles.footerLogo}>
              DontCry <span style={{fontWeight: '30'}}>ai</span>
            </h3>
            <p style={styles.footerTagline}>Trusted Intelligence. Confident Care.</p>
          </div>
          <div style={styles.footerRight}>
            <a href="mailto:dontcrytech@gmail.com" style={styles.footerEmail}>
              dontcrytech@gmail.com
            </a>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p style={styles.footerCopyright}>© 2025 DontCry ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

const keyframes = `

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
    @keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }


  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(1.05);
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }



  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(139, 92, 246, 0.3), 0 0 40px rgba(139, 92, 246, 0.2);
    }
    50% {
      box-shadow: 0 0 30px rgba(139, 92, 246, 0.5), 0 0 60px rgba(139, 92, 246, 0.3);
    }
  }

  .pricingCard {
    transition: all 0.3s ease;
  }

  .pricingCard:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(139, 92, 246, 0.2);
  }

@media (max-width: 1024px) {
  .navbarMenu {
    display: none !important;
  }
  .mobileMenuButton {
    display: flex !important;
  }
}

@media (min-width: 1025px) {
  .mobileMenuButton {
    display: none !important;
  }
  .mobileMenu {
    display: none !important;
  }
  .mobileSignInButton {
    display: none !important;
  }
}

  @media (min-width: 1025px) {
    .mobileMenuButton {
      display: none !important;
    }
    .mobileMenu {
      display: none !important;
    }
  }

  @media (min-width: 768px) {
    .tabDesc {
      display: block !important;
    }
  }
`;

const styles = {

warningBanner: {
  display: 'flex',
  gap: '14px',
  padding: '18px 20px',
  backgroundColor: '#FFFFFF',
  borderRadius: '14px',
  border: '1px solid #E5E7EB',
  marginTop: '24px',
  alignItems: 'center',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s ease',
},
warningIconWrapper: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '42px',
  height: '42px',
  backgroundColor: '#F0F9FF',
  borderRadius: '10px',
  flexShrink: 0,
  border: '1px solid #DBEAFE',
},
warningIcon: {
  fontSize: '20px',
  lineHeight: 1,
},
warningContent: {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
},
warningTitle: {
  fontSize: '14px',
  fontWeight: '600',
  color: '#1F2937',
  display: 'block',
  marginBottom: '2px',
},
warningText: {
  fontSize: '13px',
  color: '#6B7280',
  lineHeight: '1.5',
  fontWeight: '400',
  margin: 0,
},
  page: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    backgroundColor: '#FFFFFF',
    minHeight: '100vh',
    paddingTop: '72px',
  },
  navbar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  },
  navbarScrolled: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.05)'
  },
  navbarContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 32px'
  },
  navbarContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '72px'
  },
  navbarLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
  },
  navbarLogoImage: {
    width: '32px',
    height: '32px',
    objectFit: 'contain'
  },
  navbarLogoText: {
    letterSpacing: '-0.01em'
  },
  navbarMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: '40px'
  },
  navbarLink: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#6B7280',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    position: 'relative',
  },
  signInButton: {
  padding: '10px 20px',
  background: 'linear-gradient(135deg, #111827, #1f2937)',
  color: '#ffffff',
  borderRadius: '30px',
  border: '1px solid rgba(255,255,255,0.08)',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '400',
  letterSpacing: '0.4px',
  transition: 'all 0.35s ease',
  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
},

profileCircle: {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: '500',
  fontFamily: "'Poppins', sans-serif",
  border: '1.5px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  letterSpacing: '0.3px',
  ':hover': {
    transform: 'translateY(-2px)',
    border: '1.5px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
    background: 'linear-gradient(135deg, #2a2a2a 0%, #0a0a0a 100%)',
  }
},

dropdownMenu: {
  position: 'absolute',
  top: '50px',
  right: '0',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  minWidth: '240px',
  zIndex: 1000,
  overflow: 'hidden',
  animation: 'slideDown 0.2s ease-out',
  border: '1px solid #E5E7EB',
},

dropdownHeader: {
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  backgroundColor: '#F9FAFB',
},

dropdownAvatar: {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Poppins', sans-serif",
  fontSize: '16px',
  fontWeight: '500',
  flexShrink: 0,
},

dropdownUserInfo: {
  flex: 1,
  minWidth: 0,
},

dropdownEmail: {
  fontSize: '14px',
  fontWeight: '500',
  color: '#1F2937',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
},

dropdownDivider: {
  height: '1px',
  backgroundColor: '#E5E7EB',
  margin: '0',
},

dropdownItem: {
  width: '100%',
  padding: '12px 16px',
  textAlign: 'left',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
},

mobileMenu: {
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  backgroundColor: 'white',
  borderTop: '1px solid #E5E7EB',
  padding: '16px 0',
  width: '100%',
},

mobileMenuLink: {
  padding: '14px 24px',
  textAlign: 'left',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: '500',
  color: '#1F2937',
  transition: 'all 0.2s ease',
  width: '100%',
  display: 'block',
},

mobileUserSection: {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '16px 24px 8px 24px',
  borderTop: '1px solid #E5E7EB',
  marginTop: '8px',
},

mobileUserInfo: {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  backgroundColor: '#F9FAFB',
  borderRadius: '12px',
  width: '100%',
  boxSizing: 'border-box',
},

mobileAvatar: {
  width: '44px',
  height: '44px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '17px',
  fontWeight: '500',
  fontFamily: "'Poppins', sans-serif",
  flexShrink: 0,
  border: '1.5px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  letterSpacing: '0.3px',
},

mobileEmailText: {
  fontSize: '14px',
  fontWeight: '500',
  color: '#1F2937',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
},


mobileMenuButtonStyle: {
  display: 'none',
  background: 'none',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  color: '#1F2937',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
},

mobileHeaderActions: {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
},

mobileSignInButton: {
  padding: '8px 16px',
  background: 'linear-gradient(135deg, #111827, #1f2937)',
  color: '#ffffff',
  borderRadius: '20px',
  border: '1px solid rgba(255,255,255,0.08)',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '400',
  letterSpacing: '0.3px',
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  whiteSpace: 'nowrap',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
},
mobileMenuDivider: {
  height: '1px',
  backgroundColor: '#E5E7EB',
  margin: '8px 24px',
},

mobileMenuSignInButton: {
  margin: '8px 24px 16px 24px',
  padding: '13px',
  textAlign: 'center',
  backgroundColor: '#0b0f17ff',
  color: '#ffffff',
  border: 'none',
  borderRadius: '14px',
  cursor: 'pointer',
  fontSize: '15px',
  fontWeight: '600',
  transition: 'all 0.2s ease',
  width: 'calc(100% - 48px)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
},

mobileMenuButtonStyle: {
  display: 'none',
  background: 'none',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '6px',
  color: '#1F2937',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
  height: '36px',
  width: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
},

mobileLogoutButton: {
  padding: '12px 16px',
  textAlign: 'left',
  backgroundColor: '#fefefeff',
  border: '1px solid #c7c5c5ff',
  borderRadius: '10px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600',
  color: '#4e4e4eff',
  transition: 'all 0.2s ease',
  width: '100%',
  display: 'flex',
  alignItems: 'left',
  justifyContent: 'left',
  boxSizing: 'border-box',
  marginTop: '4px',
},

  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  title: {
    fontSize: 'clamp(36px, 6vw, 54px)',
    fontWeight: '300',
    color: '#111827',
    marginBottom: '16px',
    letterSpacing: '-0.03em',
    lineHeight: '1.2',
  },
  titleHighlight: {
    color: '#8B5CF6',
    fontWeight: '500',
  },
  subtitle: {
    fontSize: 'clamp(16px, 2.5vw, 19px)',
    color: '#6B7280',
    fontWeight: '400',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
    border: '1px solid #F3F4F6',
    overflow: 'hidden',
    transition: 'box-shadow 0.3s ease',
  },
  tabs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0',
    borderBottom: '1px solid #F3F4F6',
    backgroundColor: '#FAFAFA',
  },
  tabButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '20px 12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderBottom: '3px solid transparent',
    color: '#6B7280',
  },
  tabButtonActive: {
    backgroundColor: 'white',
    borderBottom: '3px solid #8B5CF6',
    color: '#111827',
  },
  tabText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
  },
  tabLabel: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '2px',
  },
  tabDesc: {
    fontSize: '13px',
    color: '#9CA3AF',
    fontWeight: '400',
    display: 'none',
  },
  tabContentWrapper: {
    padding: '40px',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  toast: {
    position: 'fixed',
    top: '100px',
    right: '20px',
    padding: '16px 24px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    zIndex: 1001,
    animation: 'slideDown 0.3s ease-out',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  },
  toastSuccess: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
    border: '1px solid #10B981',
  },
  toastError: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    border: '1px solid #EF4444',
  },
  toastIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  trialBanner: {
    backgroundColor: '#F3E8FF',
    borderRadius: '16px',
    padding: '16px 24px',
    marginBottom: '24px',
    border: '1px solid #E9D5FF',
    animation: 'fadeIn 0.5s ease-out',
  },
  trialBannerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  trialBannerText: {
    fontSize: '15px',
    color: '#6B21A8',
    fontWeight: '500',
  },
  uploadArea: {
    border: '2px dashed #E5E7EB',
    borderRadius: '20px',
    padding: '60px 40px',
    textAlign: 'center',
    backgroundColor: '#FAFAFA',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  uploadAreaActive: {
    borderColor: '#8B5CF6',
    backgroundColor: '#F3E8FF',
    transform: 'scale(1.02)',
  },
  uploadIcon: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'center',
  },
  uploadTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '8px',
    letterSpacing: '-0.01em',
  },
  uploadDescription: {
    fontSize: '15px',
    color: '#6B7280',
    marginBottom: '24px',
  },
  uploadButton: {
    padding: '12px 32px',
    backgroundColor: '#111827',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 16px rgba(17, 24, 39, 0.15)',
  },
  uploadNote: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginTop: '20px',
  },
  filePreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    backgroundColor: '#F9FAFB',
    borderRadius: '16px',
    border: '1px solid #E5E7EB',
    animation: 'fadeIn 0.3s ease-out',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  fileIconWrapper: {
    width: '48px',
    height: '48px',
    backgroundColor: '#F3E8FF',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileName: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '4px',
  },
  fileSize: {
    fontSize: '13px',
    color: '#6B7280',
  },
  fileRemove: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6B7280',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
  },
  errorMessage: {
    padding: '16px 20px',
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    animation: 'slideDown 0.3s ease-out',
  },
  predictButton: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#8B5CF6',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
  },
  predictButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  skeletonContainer: {
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  skeletonCircle: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    background: 'linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  skeletonLine: {
    width: '80%',
    height: '20px',
    borderRadius: '10px',
    background: 'linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  skeletonButton: {
    width: '60%',
    height: '40px',
    borderRadius: '12px',
    background: 'linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  recordControl: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  recordIconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  recordIcon: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    backgroundColor: '#F3E8FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(139, 92, 246, 0.2)',
  },
  recordIconActive: {
    backgroundColor: '#FEE2E2',
    animation: 'pulse 2s ease-in-out infinite',
    boxShadow: '0 4px 30px rgba(239, 68, 68, 0.3)',
  },
  recordTime: {
    fontSize: '36px',
    fontWeight: '300',
    color: '#111827',
    marginBottom: '24px',
    letterSpacing: '-0.02em',
  },
  recordControls: {
    marginBottom: '20px',
  },
  recordButton: {
    padding: '14px 36px',
    backgroundColor: '#8B5CF6',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
  },
  recordButtonStop: {
    backgroundColor: '#EF4444',
    boxShadow: '0 4px 16px rgba(239, 68, 68, 0.3)',
  },
  recordNote: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: '400',
  },
  audioPreview: {
    backgroundColor: '#F9FAFB',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #E5E7EB',
    animation: 'fadeIn 0.3s ease-out',
  },
  audioPreviewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  audioPreviewTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
  },
  audioDelete: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6B7280',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
  },
  audioPlayer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  playButton: {
    width: '48px',
    height: '48px',
    backgroundColor: '#8B5CF6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
  },
  audioInfo: {
    flex: 1,
  },
  audioMeta: {
    display: 'flex',
    justifyContent: 'space-between',
fontSize: '13px',
color: '#6B7280',
marginBottom: '8px',
},
progressBar: {
width: '100%',
height: '6px',
backgroundColor: '#E5E7EB',
borderRadius: '100px',
overflow: 'hidden',
},
progressFill: {
height: '100%',
backgroundColor: '#8B5CF6',
borderRadius: '100px',
transition: 'width 0.3s ease',
},
loadingIndicator: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
gap: '12px',
padding: '20px',
color: '#6B7280',
fontSize: '14px',
},
spinner: {
width: '20px',
height: '20px',
border: '3px solid #E5E7EB',
borderTop: '3px solid #8B5CF6',
borderRadius: '50%',
animation: 'spin 1s linear infinite',
},
predictionsList: {
marginTop: '24px',
},
predictionsTitle: {
fontSize: '18px',
fontWeight: '600',
color: '#111827',
marginBottom: '16px',
},
predictionsItems: {
display: 'flex',
flexDirection: 'column',
gap: '12px',
},
predictionItem: {
backgroundColor: '#F9FAFB',
borderRadius: '12px',
padding: '16px 20px',
border: '1px solid #E5E7EB',
transition: 'all 0.3s ease',
animation: 'slideIn 0.3s ease-out',
},
predictionContent: {
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
},
predictionInfo: {
flex: 1,
},
predictionClass: {
fontSize: '16px',
fontWeight: '600',
color: '#111827',
marginBottom: '4px',
textTransform: 'capitalize',
},
predictionTime: {
fontSize: '13px',
color: '#6B7280',
},
predictionConfidence: {
padding: '6px 14px',
borderRadius: '100px',
fontSize: '14px',
fontWeight: '600',
color: 'white',
},
emptyState: {
textAlign: 'center',
padding: '60px 20px',
color: '#9CA3AF',
fontSize: '15px',
},
successBadge: {
  fontSize: '15px',
  fontWeight: '600',
  color: '#10B981',
  textAlign: 'center',
  marginBottom: '20px',
  animation: 'slideDown 0.3s ease-out',
},
resultContainer: {
display: 'flex',
flexDirection: 'column',
gap: '24px',
marginTop: '32px',
},
resultCard: {
backgroundColor: 'white',
borderRadius: '20px',
padding: '40px',
textAlign: 'center',
border: '1px solid #F3F4F6',
boxShadow: '0 10px 40px rgba(139, 92, 246, 0.12)',
},
resultIcon: {
width: '100px',
height: '100px',
borderRadius: '50%',
margin: '0 auto 24px',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
animation: 'pulse 2s ease-in-out infinite',
},
resultEmoji: {
fontSize: '48px',
},
resultTitle: {
fontSize: '32px',
fontWeight: '500',
color: '#111827',
marginBottom: '12px',
letterSpacing: '-0.02em',
},
resultAdvice: {
fontSize: '16px',
color: '#6B7280',
marginBottom: '28px',
lineHeight: '1.6',
},
confidenceBadge: {
display: 'inline-flex',
alignItems: 'center',
gap: '12px',
padding: '12px 24px',
backgroundColor: '#F3E8FF',
borderRadius: '100px',
},
confidenceLabel: {
fontSize: '14px',
color: '#6B7280',
fontWeight: '500',
},
confidenceValueWrapper: {
display: 'flex',
alignItems: 'center',
},
confidenceValue: {
fontSize: '20px',
fontWeight: '700',
color: '#8B5CF6',
},
probabilitiesCard: {
backgroundColor: '#FAFAFA',
borderRadius: '20px',
padding: '32px',
border: '1px solid #F3F4F6',
},
probabilitiesTitle: {
fontSize: '18px',
fontWeight: '600',
color: '#111827',
marginBottom: '24px',
},
probabilitiesList: {
display: 'flex',
flexDirection: 'column',
gap: '16px',
},
probabilityItem: {
display: 'grid',
gridTemplateColumns: '140px 1fr 60px',
alignItems: 'center',
gap: '16px',
},
probabilityLabel: {
display: 'flex',
alignItems: 'center',
gap: '8px',
},
probabilityEmoji: {
fontSize: '20px',
},
probabilityName: {
fontSize: '15px',
fontWeight: '500',
color: '#111827',
},
probabilityBar: {
height: '8px',
backgroundColor: '#E5E7EB',
borderRadius: '100px',
overflow: 'hidden',
},
probabilityFill: {
height: '100%',
borderRadius: '100px',
transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
},
probabilityPercent: {
fontSize: '14px',
fontWeight: '600',
color: '#6B7280',
textAlign: 'right',
},
trustIndicators: {
display: 'flex',
justifyContent: 'center',
gap: '24px',
marginTop: '16px',
flexWrap: 'wrap',
},
trustItem: {
display: 'flex',
alignItems: 'center',
gap: '8px',
fontSize: '13px',
color: '#6B7280',
fontWeight: '500',
},
parentMessage: {
backgroundColor: '#FEF3C7',
borderRadius: '16px',
padding: '20px 24px',
border: '1px solid #FDE047',
marginTop: '8px',
},
parentMessageText: {
fontSize: '14px',
color: '#78350F',
lineHeight: '1.7',
margin: 0,
},
careInstructionsCard: {
backgroundColor: 'white',
borderRadius: '20px',
padding: '32px',
border: '1px solid #E0E7FF',
boxShadow: '0 4px 20px rgba(139, 92, 246, 0.08)',
},
careInstructionsHeader: {
display: 'flex',
alignItems: 'center',
gap: '12px',
marginBottom: '24px',
},
careInstructionsTitle: {
fontSize: '18px',
fontWeight: '600',
color: '#111827',
margin: 0,
},
careInstructionsContent: {
marginBottom: '24px',
},
instructionsList: {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
},
instructionItem: {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  lineHeight: '1.6',
},
instructionNumber: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '28px',
  width: '28px',
  height: '28px',
  backgroundColor: '#8B5CF6',
  color: 'white',
  borderRadius: '50%',
  fontSize: '13px',
  fontWeight: '600',
  flexShrink: 0,
  marginTop: '2px',
},
instructionText: {
  fontSize: '14px',
  color: '#374151',
  lineHeight: '1.6',
  flex: 1,
  paddingTop: '2px',
  wordBreak: 'break-word',
},

emergencyNote: {
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  padding: '20px',
  border: '1px solid #E5E7EB',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
},
emergencyContent: {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
},
emergencyTitle: {
  fontSize: '15px',
  fontWeight: '600',
  color: '#111827',
  margin: 0,
  lineHeight: '1.4',
  letterSpacing: '-0.01em',
},
emergencyText: {
  fontSize: '14px',
  color: '#6B7280',
  lineHeight: '1.6',
  margin: 0,
  fontWeight: '400',
},
helpfulNote: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px 20px',
  marginTop: '40px',  // More space
  textAlign: 'center',
  flexWrap: 'wrap'
},

noteText: {
  fontSize: '13px',
  color: '#4b5563',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: '400',
  lineHeight: '1.5',
  maxWidth: '800px'
},
pricingSection: {
marginTop: '80px',
marginBottom: '40px',
},
pricingHeader: {
textAlign: 'center',
marginBottom: '48px',
},
pricingTitle: {
fontSize: 'clamp(32px, 5vw, 42px)',
fontWeight: '500',
color: '#111827',
marginBottom: '12px',
letterSpacing: '-0.02em',
},
pricingSubtitle: {
fontSize: '18px',
color: '#6B7280',
marginBottom: '24px',
},
availabilityBadge: {
display: 'inline-block',
padding: '10px 20px',
backgroundColor: '#1F2937',
color: 'white',
borderRadius: '100px',
fontSize: '14px',
fontWeight: '600',
},
pricingGrid: {
display: 'grid',
gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
gap: '24px',
maxWidth: '1200px',
margin: '0 auto',
},
pricingCard: {
backgroundColor: 'white',
borderRadius: '24px',
padding: '40px 32px',
border: '2px solid #F3F4F6',
position: 'relative',
overflow: 'hidden',
},
pricingCardHighlighted: {
backgroundColor: '#F9FAFB',
border: '2px solid #8B5CF6',
animation: 'glow 3s ease-in-out infinite',
},
pricingBadge: {
position: 'absolute',
top: '20px',
right: '20px',
padding: '6px 14px',
backgroundColor: '#8B5CF6',
color: 'white',
borderRadius: '100px',
fontSize: '12px',
fontWeight: '600',
},
planName: {
fontSize: '24px',
fontWeight: '600',
color: '#111827',
marginBottom: '16px',
},
planPrice: {
display: 'flex',
alignItems: 'baseline',
gap: '4px',
marginBottom: '12px',
},
priceAmount: {
fontSize: '48px',
fontWeight: '700',
color: '#111827',
letterSpacing: '-0.03em',
},
pricePeriod: {
fontSize: '16px',
color: '#9CA3AF',
fontWeight: '500',
},
savingsBadge: {
display: 'inline-block',
padding: '6px 12px',
backgroundColor: '#D1FAE5',
color: '#065F46',
borderRadius: '100px',
fontSize: '13px',
fontWeight: '600',
marginBottom: '24px',
},
featureList: {
listStyle: 'none',
padding: 0,
margin: '24px 0',
display: 'flex',
flexDirection: 'column',
gap: '12px',
},
featureItem: {
display: 'flex',
alignItems: 'center',
gap: '12px',
fontSize: '15px',
color: '#4B5563',
},
featureText: {
color: '#111827',
fontWeight: '400',
},
planButton: {
width: '100%',
padding: '14px',
backgroundColor: '#F3F4F6',
color: '#6B7280',
border: 'none',
borderRadius: '12px',
fontSize: '16px',
fontWeight: '600',
cursor: 'not-allowed',
transition: 'all 0.3s ease',
},
planButtonHighlighted: {
backgroundColor: '#8B5CF6',
color: 'white',
},
footer: {
  backgroundColor: '#ffffff',
  borderTop: '1px solid #e5e7eb',
  padding: '60px 20px 30px'
},
footerContent: {
  maxWidth: '1200px',
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '40px',
  flexWrap: 'wrap',
  gap: '30px'
},
footerBrand: {
  flex: '1 1 250px',
  minWidth: '200px'
},
footerLogo: {
  fontSize: '20px',
  fontWeight: '600',
  fontFamily: 'Poppins, sans-serif',
  color: '#1a1a1a',
  margin: '0 0 12px 0',
  letterSpacing: '-0.5px'
},
footerTagline: {
  fontSize: '14px',
  color: '#6b7280',
  margin: 0,
  fontFamily: 'Poppins, sans-serif'
},
footerRight: {
  textAlign: 'right',
  flex: '0 1 auto'
},
footerEmail: {
  fontSize: '14px',
  color: '#4b5563',
  margin: 0,
  fontFamily: 'Poppins, sans-serif',
  wordBreak: 'break-word'
},
footerBottom: {
  maxWidth: '1200px',
  margin: '0 auto',
  textAlign: 'center',
  paddingTop: '30px',
  borderTop: '1px solid #e5e7eb'
},
footerCopyright: {
  fontSize: '13px',
  color: '#9ca3af',
  margin: 0,
  fontFamily: 'Poppins, sans-serif'
},

// Add media query styles
'@media (max-width: 768px)': {
  footer: {
    padding: '40px 20px 20px'
  },
  footerContent: {
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '30px'
  },
  footerRight: {
    textAlign: 'left'
  },
  footerBottom: {
    paddingTop: '20px'
  },
  footerLogo: {
    fontSize: '18px'
  }
}
};